/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { AxiosResponse } from 'axios';
import { httpService } from 'src/core/services/api/httpService';
import { OperationStatusBaseSwagger } from 'src/core/services/api/operationSwagger';
import { ErrorBody } from 'src/core/types/ErrorBody';
import { LuisRequestOptions } from 'src/core/types/LuisRequestOptions';
import { getEnvironment } from 'src/core/utils/api/environment';
import { OperationDelayStrategies } from 'src/core/utils/api/operationDelayStrategies';
import { OperationRetryStrategies } from 'src/core/utils/api/operationRetryStrategies';
import { GlobalEnvironmentMetadata } from 'src/core/utils/GlobalEnvironmentMetadata';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { ModelJobStates } from 'src/modules/modelTraining/types/trainingTypes';

/**
 * Represents a handler for the long running calls service
 * to handle the response after each hit.
 */
export type OperationHandler<T> = {
    /**
     * Determines whether the condition isn't satisfied
     * so the service hit the API again or not. Retries if
     * value returned is true and doesn't retry if returned
     * value is false.
     */
    retryStrategy: (response: AxiosResponse<T>) => boolean;

    /**
     * Determines the duration that the next call should wait.
     */
    timeToDelay: (response?: AxiosResponse<T>, trialNumber?: number) => number;

    /**
     * Function to update the store after each API call.
     */
    handleCall?: ((data: T, response?: AxiosResponse<T>) => void) | ((data: T, response?: AxiosResponse<T>) => Promise<void>);
};

type UrlBuilder = (trial?: number) => string;
const { MAX_TRIALS, AWAIT_TIME } = LuisConstants.operationCall;

/**
 * It's a workaround to replace the hostname of the long running operations status url
 * with the custom domain until the backend fixes this issue on their side.
 */
export const getCustomDomainUrl = (urlString: string, urlAsIs: boolean = false) => {
    if (!getEnvironment().isProduction || urlAsIs) {
        return urlString;
    }

    const { endpoint } = GlobalEnvironmentMetadata.instance;

    const urlObj = new URL(urlString);

    return `${endpoint}${urlObj.pathname}${urlObj.search}`;
};

/**
 * Creates a long running Api call that hits the given url with the given api options
 * many times and handle each response with the given handler.
 * The long running operation stops when the retry strategy condition matched in the given handler.
 *
 * @param url The url to hit
 * @param apiOptions The options of the api
 * @param handle The handler function to handle each api response
 * @param hitsCount Determines the number of the Api calls
 * @param onHttpCall An optional override to the default GET operation.
 * @param maxTrials An optional override to the default MAX_TRIALS.
 * @param urlAsIs An optional boolean to keep sent url as it is.
 */
export const createOperationCall = async <T>(
    url: string | UrlBuilder,
    apiOptions: LuisRequestOptions,
    handler: OperationHandler<T>,
    hitsCount: number = 0,
    onHttpCall?: () => Promise<AxiosResponse<T>>,
    maxTrials: number = MAX_TRIALS,
    urlAsIs: boolean = false
): Promise<AxiosResponse<T>> => {
    try {
        for (let i = 0; i < maxTrials; i++) {
            const urlToCall = getCustomDomainUrl(typeof url === 'string' ? url : url(hitsCount), urlAsIs);

            const response = await (onHttpCall ? onHttpCall() : httpService.get<T>(urlToCall, apiOptions));

            if (handler.handleCall !== undefined) {
                await handler.handleCall(response.data, response);
            }

            if (!handler.retryStrategy(response)) {
                return response;
            }

            // Wait for the specific time that the handler decides.
            await new Promise(resolve => setTimeout(resolve, AWAIT_TIME));

            hitsCount++;
        }
    } catch (err) {
        await Promise.reject(err);
    }
};

/**
 * Creates a long running operation and polls it till completion
 * based on what completion means in the passed handler.
 *
 * @param initOperationCall The initial operation that triggers
 * the long running operation.
 * @param apiOptions The API options to pass with the HTTP request.
 * @param handler The handler that parses each poll response from
 * the long running operation.
 * @param onGetPollUrl An optional function to extract the URL to poll
 * from the `initOperationCall` returned response. Defaults to trying
 * to read the URL to call from the location header.
 * @param maxTrials An optional value to override the max number of trials
 */
export const createOperationCallAndPoll = async <T extends OperationStatusBaseSwagger>(
    initOperationCall: Promise<AxiosResponse>,
    apiOptions: LuisRequestOptions,
    handler?: OperationHandler<T>,
    onGetPollUrl?: (response: AxiosResponse<T>) => string,
    maxTrials?: number
): Promise<AxiosResponse<T>> => {
    const response = await initOperationCall;
    const pollUrl = onGetPollUrl
        ? onGetPollUrl(response)
        : (<Record<string, string>>response.headers)[LuisConstants.OperationHeaders.NewLocationHeader];

    const pollHandler: OperationHandler<T> = handler ?? {
        retryStrategy: OperationRetryStrategies.defaultRetryStrategy,
        timeToDelay: OperationDelayStrategies.exponentialDelayStrategy
    };

    const pollResponse = await createOperationCall(pollUrl, apiOptions, pollHandler, maxTrials);

    if (pollResponse.data.status === 'failed') {
        const e = new Error();

        if (pollResponse.data.errors?.length) {
            e.name = pollResponse.data.errors[0].code ?? pollResponse.data.errors[0].error?.code;
            e.message = pollResponse.data.errors[0].message ?? pollResponse.data.errors[0].error.message;
        }

        throw e;
    }

    return pollResponse;
};

export const createOperationCallWithJobSwaggers = async <T extends { status: ModelJobStates; errors: ErrorBody[] }>(
    url: string | UrlBuilder,
    apiOptions: LuisRequestOptions,
    handler: OperationHandler<T>,
    hitsCount: number = 0,
    onHttpCall?: () => Promise<AxiosResponse<T>>
): Promise<AxiosResponse<T>> => {
    try {
        for (let i = 0; i < MAX_TRIALS; i++) {
            const urlToCall = getCustomDomainUrl(typeof url === 'string' ? url : url(hitsCount));

            const response = await (onHttpCall ? onHttpCall() : httpService.get<T>(urlToCall, apiOptions));

            if (handler.handleCall !== undefined) {
                await handler.handleCall(response.data, response);
            }

            if (!handler.retryStrategy(response)) {
                if (response.data.status === 'failed') {
                    const e = new Error();
                    if (response.data.errors?.length) {
                        e.name = response.data.errors[0].code;
                        e.message = response.data.errors[0].message;
                    }
                    throw e;
                }

                return response;
            }

            // Wait for the specific time that the handler decides.
            await new Promise(resolve => setTimeout(resolve, AWAIT_TIME));

            hitsCount++;
        }
    } catch (err) {
        await Promise.reject(err);
    }
};
